import { DdnsJetonPostes, DdnsModeEnrolement, DdnsPoste, DdnsServeur, DdnsStatutPoste } from '../types';

/** 3 à 40 caractères parmi a-z, 0-9 et tiret, ni tiret en tête ni en queue : la règle du serveur. */
export const NOM_SERVEUR_REGEX = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/;

export const nomServeurValide = (nom: string): boolean => NOM_SERVEUR_REGEX.test(nom);

const NOM_MAX = 40;

const couperNom = (nom: string, longueur: number): string => nom.slice(0, longueur).replace(/-+$/, '');

/**
 * Nom proposé à partir du nom du client : minuscules, sans accents, tirets. Si le nom est
 * déjà pris, suffixe -2, -3… ; un nom sans lettre latine (ex. en arabe) devient client-<id>.
 * Le staff peut le modifier ; le serveur contrôle l'unicité.
 */
export const proposerNomServeur = (nomClient: string, idClient: number, nomsPris: string[]): string => {
  let base = nomClient
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  base = couperNom(base, NOM_MAX);
  if (base.length < 3) {
    base = `client-${idClient}`;
  }
  const pris = new Set(nomsPris);
  if (!pris.has(base)) {
    return base;
  }
  for (let rang = 2; ; rang += 1) {
    const suffixe = `-${rang}`;
    const candidat = `${couperNom(base, NOM_MAX - suffixe.length)}${suffixe}`;
    if (!pris.has(candidat)) {
      return candidat;
    }
  }
};

/** Date du jour à Alger, AAAA-MM-JJ : celle qu'utilise le serveur. */
export const dateAlger = (maintenant: Date = new Date()): string => {
  const parties = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Africa/Algiers',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(maintenant);
  const valeur = (type: string) => parties.find((partie) => partie.type === type)?.value ?? '';
  return `${valeur('year')}-${valeur('month')}-${valeur('day')}`;
};

/** AAAA-MM-JJ + 1 an ; un 29 février donne le 28 février, comme PostgreSQL. */
export const ajouterUnAn = (iso: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) {
    return '';
  }
  const annee = Number(match[1]) + 1;
  const mois = Number(match[2]);
  const dernierJour = new Date(Date.UTC(annee, mois, 0)).getUTCDate();
  const jour = Math.min(Number(match[3]), dernierJour);
  return `${annee}-${match[2]}-${String(jour).padStart(2, '0')}`;
};

export type Connexion = 'en_ligne' | 'hors_ligne' | 'jamais_vu';

/** Dernier battement : en ligne (moins de 10 minutes, calculé par le serveur), hors ligne, jamais vu. */
export const connexion = (item: Pick<DdnsServeur, 'vu_le' | 'en_ligne'>): Connexion => {
  if (!item.vu_le) {
    return 'jamais_vu';
  }
  return item.en_ligne ? 'en_ligne' : 'hors_ligne';
};

/** « à l'instant », « il y a 5 min », « il y a 3 h », « il y a 2 j », « jamais ». */
export const formatVuIlYa = (iso: string | null, maintenant: Date = new Date()): string => {
  if (!iso) {
    return 'jamais';
  }
  const instant = new Date(iso).getTime();
  if (Number.isNaN(instant)) {
    return iso;
  }
  const minutes = Math.floor((maintenant.getTime() - instant) / 60000);
  if (minutes < 1) {
    return "à l'instant";
  }
  if (minutes < 60) {
    return `il y a ${minutes} min`;
  }
  const heures = Math.floor(minutes / 60);
  if (heures < 24) {
    return `il y a ${heures} h`;
  }
  return `il y a ${Math.floor(heures / 24)} j`;
};

/** Horodatage ISO du serveur -> « 17/09/2026 à 08:12 » (heure d'Alger, telle qu'écrite). */
export const formatHorodatage = (iso: string | null): string => {
  if (!iso) {
    return '-';
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso);
  return match ? `${match[3]}/${match[2]}/${match[1]} à ${match[4]}:${match[5]}` : iso;
};

export type PastilleExpiration = 'actif' | 'bientot' | 'grace' | 'expire' | 'suspendu';

export const SEUIL_BIENTOT_JOURS = 30;

/** Pastille d'expiration : suspendu, expiré, délai de grâce, expire dans moins de 30 jours, actif. */
export const pastilleExpiration = (
  item: Pick<DdnsServeur, 'etat' | 'jours_restants'>
): { code: PastilleExpiration; libelle: string } => {
  if (item.etat === 'SUSPENDU') {
    return { code: 'suspendu', libelle: 'Suspendu' };
  }
  if (item.etat === 'EXPIRE') {
    return { code: 'expire', libelle: 'Expiré' };
  }
  if (item.jours_restants < 0) {
    return { code: 'grace', libelle: 'Expiré, accès maintenu' };
  }
  if (item.jours_restants === 0) {
    return { code: 'bientot', libelle: "Expire aujourd'hui" };
  }
  if (item.jours_restants < SEUIL_BIENTOT_JOURS) {
    return { code: 'bientot', libelle: `Expire dans ${item.jours_restants} j` };
  }
  return { code: 'actif', libelle: 'Actif' };
};

/** Bloc à coller dans WinalcoFichiers.ini, sur le serveur du client. */
export const blocIniServeur = (nom: string, jeton: string): string =>
  `[DDNS]\nActif=1\nServer_Name=${nom}\nToken=${jeton}\n`;

/**
 * Bloc à coller dans l'ini d'un poste distant (Winalco_Scf.ini, Gscom.ini, PAIE.ini).
 *
 * Avec un jeton de postes : le poste s'en sert pour demander l'adresse de son serveur sans
 * licence à lui — c'est justement ce que la licence réseau supprime. La même valeur pour
 * tous les postes du client, et elle ne change pas quand un poste est formaté ou remplacé.
 * Sans jeton : la forme d'avant, le poste s'identifiant par sa propre licence.
 */
export const blocIniPoste = (nom: string, jetonPostes?: string): string =>
  `[DDNS]\nActif=1\nServer_Name=${nom}\n` +
  (jetonPostes ? `Jeton_Postes=${jetonPostes}\n` : '');

// ---- Jetons d'enrôlement et postes (politique du 03/10/2026) --------------------------------

/** Durée maximale pendant laquelle un jeton peut encore servir directement (refusée au-delà par le serveur). */
export const TOLERANCE_DIRECT_JOURS = 60;

/** AAAA-MM-JJ + n jours (calendrier, sans fuseau). */
export const ajouterJours = (iso: string, jours: number): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) {
    return '';
  }
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + jours));
  return date.toISOString().slice(0, 10);
};

export const LIBELLES_MODE: Record<DdnsModeEnrolement, string> = {
  APPROBATION: 'Sur approbation',
  OUVERT: 'Ouvert',
  FERME: 'Fermé',
};

export const AIDES_MODE: Record<DdnsModeEnrolement, string> = {
  APPROBATION: 'Chaque nouveau poste attend votre validation ici.',
  OUVERT: 'Un nouveau poste est accepté d’emblée tant que le plafond n’est pas atteint, puis attend.',
  FERME: 'Aucun nouveau poste ; les postes déjà autorisés continuent.',
};

export const LIBELLES_STATUT_POSTE: Record<DdnsStatutPoste, string> = {
  EN_ATTENTE: 'En attente',
  AUTORISE: 'Autorisé',
  REFUSE: 'Refusé',
  REVOQUE: 'Révoqué',
};

/**
 * Pastille d'un poste. Un poste autorisé dont le jeton est révoqué n'obtient plus l'adresse : il
 * s'affiche « Coupé (jeton révoqué) », pour que la page ne dise pas « Autorisé » à tort.
 */
export const pastillePoste = (
  poste: Pick<DdnsPoste, 'statut' | 'id_jeton'>,
  jetons: Pick<DdnsJetonPostes, 'id' | 'etat'>[]
): { code: 'attente' | 'autorise' | 'coupe'; libelle: string } => {
  const jetonRevoque = jetons.find((jeton) => jeton.id === poste.id_jeton)?.etat === 'REVOQUE';
  if (poste.statut === 'REFUSE' || poste.statut === 'REVOQUE') {
    return { code: 'coupe', libelle: LIBELLES_STATUT_POSTE[poste.statut] };
  }
  if (jetonRevoque) {
    return { code: 'coupe', libelle: 'Coupé (jeton révoqué)' };
  }
  return poste.statut === 'EN_ATTENTE'
    ? { code: 'attente', libelle: 'En attente' }
    : { code: 'autorise', libelle: 'Autorisé' };
};

/** Nom affiché d'un poste : celui donné par le staff, sinon la machine, sinon son code. */
export const nomPoste = (poste: Pick<DdnsPoste, 'libelle' | 'nom_machine' | 'code'>): string =>
  poste.libelle || poste.nom_machine || `Poste ${poste.code}`;

/** Usage direct d'un jeton : « jusqu'au 02/12/2026 », « terminé le … », « non ». */
export const usageDirect = (jeton: Pick<DdnsJetonPostes, 'direct_jusqu_au' | 'direct_tolere'>): string => {
  if (!jeton.direct_jusqu_au) {
    return 'Non';
  }
  const [annee, mois, jour] = jeton.direct_jusqu_au.split('-');
  const date = `${jour}/${mois}/${annee}`;
  return jeton.direct_tolere ? `Jusqu’au ${date}` : `Terminé le ${date}`;
};
